import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import PlanningCycleSelector from '../PlanningCycleSelector';
import { OwnerSelector } from './OwnerSelector';
import { OKRCreate, OKRUpdate, OrganizationOKR } from '../../services/okrs';
import {
  PlanningConfiguration,
  PlanningCycle,
} from '../../services/planningCycles';
import { useOrganization } from '../../context/OrganizationContext';
import { API_URL } from '../../constants/urls';

interface OKRFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: OKRCreate | OKRUpdate, isEdit: boolean) => Promise<void>;
  okr?: OrganizationOKR | null;
  planningCycles: PlanningCycle[];
  planningConfig?: PlanningConfiguration | null;
  parentOkrs?: OrganizationOKR[];
  canSetCorporate?: boolean;
  organizationId: string;
  onCyclesUpdate?: (cycles: PlanningCycle[]) => void;
  initialParentId?: string | null;
  initialRelatedKrId?: string | null;
  initialKrDetails?: {
    description: string;
    currentUserName: string;
  } | null;
  initialFilters?: {
    cycleTypes?: string[];
    specificCycles?: string[];
    status?: string[];
    owners?: string[];
  };
}

export const OKRFormModal: React.FC<OKRFormModalProps> = ({
  open,
  onClose,
  onSave,
  okr,
  planningCycles,
  planningConfig,
  parentOkrs = [],
  canSetCorporate = false,
  organizationId,
  onCyclesUpdate,
  initialParentId = null,
  initialRelatedKrId = null,
  initialKrDetails = null,
  initialFilters,
}) => {
  const { userRole } = useOrganization();
  const isAdmin = userRole === 'admin';

  const [formData, setFormData] = useState<OKRCreate>({
    objective: '',
    description: '',
    planning_cycle_id: '',
    parent_okr_id: '',
    area_id: '',
    is_corporate: false,
    status: 'draft',
    commitment_type: 'committed',
    owner_id: '',
    key_results: [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [areas, setAreas] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([]);

  // Initialize form when modal opens or OKR changes
  useEffect(() => {
    if (okr) {
      setFormData({
        objective: okr.objective,
        description: okr.description || '',
        planning_cycle_id: okr.planning_cycle_id || '',
        parent_okr_id: okr.parent_okr_id || '',
        area_id: okr.area_id || '',
        is_corporate: okr.is_corporate,
        status: okr.status,
        commitment_type: okr.commitment_type || 'committed',
        owner_id: okr.owner_id || '',
      });
    } else {
      // Reset form for new OKR
      // Pre-populate based on current filters
      let defaultCycleId = planningCycles.find((c) => c.is_active)?.id || '';
      let defaultStatus: 'draft' | 'active' = 'draft';
      let defaultOwnerId = '';

      // If specific cycles are filtered, use the first one
      if (
        initialFilters?.specificCycles &&
        initialFilters.specificCycles.length > 0
      ) {
        const matchingCycle = planningCycles.find((c) =>
          initialFilters.specificCycles!.includes(c.id),
        );
        if (matchingCycle) defaultCycleId = matchingCycle.id;
      }
      // Otherwise, if cycle types are filtered, use the first active cycle of that type
      else if (
        initialFilters?.cycleTypes &&
        initialFilters.cycleTypes.length > 0
      ) {
        const matchingCycle =
          planningCycles.find(
            (c) =>
              initialFilters.cycleTypes!.includes(c.cycle_type) && c.is_active,
          ) ||
          planningCycles.find((c) =>
            initialFilters.cycleTypes!.includes(c.cycle_type),
          );
        if (matchingCycle) defaultCycleId = matchingCycle.id;
      }

      // If only one status is filtered, use it (if it's valid for creation)
      if (initialFilters?.status && initialFilters.status.length === 1) {
        const status = initialFilters.status[0];
        if (status === 'draft' || status === 'active') {
          defaultStatus = status as 'draft' | 'active';
        }
      }

      // If only one owner is filtered, use it
      if (
        initialFilters?.owners &&
        initialFilters.owners.length === 1 &&
        initialFilters.owners[0] !== 'unassigned'
      ) {
        defaultOwnerId = initialFilters.owners[0];
      }

      // Auto-generate objective name if KR details are provided
      let autoObjectiveName = '';
      if (initialRelatedKrId && initialKrDetails) {
        autoObjectiveName = `Objective - ${initialKrDetails.description}${
          initialKrDetails.currentUserName
            ? `: ${initialKrDetails.currentUserName}`
            : ''
        }`;
      }

      setFormData({
        objective: autoObjectiveName || '',
        description: '',
        planning_cycle_id: defaultCycleId,
        parent_okr_id: initialParentId || '',
        area_id: '',
        is_corporate: false,
        status: defaultStatus,
        commitment_type: 'committed',
        owner_id: defaultOwnerId,
        key_results: [],
      });
    }
  }, [
    okr,
    open,
    planningCycles,
    initialParentId,
    initialRelatedKrId,
    initialKrDetails,
    initialFilters,
  ]);

  // Fetch areas when modal opens
  useEffect(() => {
    if (open && organizationId) {
      const fetchAreas = async () => {
        try {
          const response = await axios.get(
            `${API_URL}/areas?organization_id=${organizationId}`,
            { headers: getAuthHeaders() },
          );
          setAreas(response.data || []);
        } catch (error) {
          console.error('Failed to fetch areas:', error);
        }
      };
      fetchAreas();
    }
  }, [open, organizationId, API_URL]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.objective.trim()) {
      setError('Objective is required');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = okr
        ? {
            objective: formData.objective,
            description: formData.description,
            status: formData.status,
            commitment_type: formData.commitment_type,
            owner_id: formData.owner_id || undefined,
            is_corporate: formData.is_corporate,
            planning_cycle_id: formData.planning_cycle_id || undefined,
          }
        : {
            ...formData,
            // Convert empty strings to undefined for optional fields
            planning_cycle_id: formData.planning_cycle_id || undefined,
            parent_okr_id: formData.parent_okr_id || undefined,
            owner_id: formData.owner_id || undefined,
            key_results: [], // No key results on initial creation
          };

      await onSave(dataToSave, !!okr);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save objective');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg sm:rounded-2xl bg-white p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex justify-between items-center mb-6"
                >
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {okr
                      ? `Edit ${okr.formatted_reference ? okr.formatted_reference + ' - ' : ''}${okr.objective}`
                      : 'Create New Objective'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 max-h-[calc(100vh-180px)] sm:max-h-[calc(100vh-200px)] overflow-y-auto pr-1 sm:pr-2"
                >
                  {/* Section 1: Core Content */}
                  <div className="space-y-4">
                    {/* Objective - Most prominent */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Objective <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.objective}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            objective: e.target.value,
                          })
                        }
                        className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                        placeholder="What do you want to achieve? (e.g., Increase customer satisfaction)"
                        required
                        autoFocus
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        A clear, measurable goal that inspires action
                      </p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={4}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Why is this objective important? What context should others know?"
                      />
                    </div>
                  </div>

                  {/* Section 2: Ownership & Classification */}
                  <div className="pt-4 border-t border-gray-200 space-y-4">
                    <h4 className="text-sm font-medium text-gray-600">
                      Ownership & Classification
                    </h4>

                    {/* Owner Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Owner (DRI - Directly Responsible Individual)
                      </label>
                      <OwnerSelector
                        organizationId={organizationId}
                        selectedOwnerId={formData.owner_id}
                        onOwnerSelect={(ownerId) =>
                          setFormData({ ...formData, owner_id: ownerId || '' })
                        }
                        placeholder="Select who will drive this objective"
                        className="mt-1"
                      />
                    </div>

                    {/* Area Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Area
                      </label>
                      <select
                        value={formData.area_id || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, area_id: e.target.value })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">No specific area</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Assign this OKR to a functional area
                      </p>
                    </div>

                    {/* Corporate Objective Toggle */}
                    {(canSetCorporate || (okr && isAdmin)) && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is-corporate"
                          checked={formData.is_corporate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_corporate: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="is-corporate"
                          className="ml-2 block text-sm text-gray-700"
                        >
                          <span className="font-medium">
                            Corporate Objective
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            - Organization-wide strategic goal
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Parent OKR */}
                    {!okr && parentOkrs.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Parent Objective
                          <span className="text-xs text-gray-500 ml-1">
                            (Optional)
                          </span>
                        </label>
                        <select
                          value={formData.parent_okr_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              parent_okr_id: e.target.value,
                            })
                          }
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                        >
                          <option value="">None - Top-level objective</option>
                          {parentOkrs.map((parentOkr) => (
                            <option key={parentOkr.id} value={parentOkr.id}>
                              {parentOkr.formatted_reference &&
                                `${parentOkr.formatted_reference} - `}
                              {parentOkr.objective}
                              {parentOkr.is_corporate && ' (Corporate)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Section 3: Planning & Measurement */}
                  <div className="pt-4 border-t border-gray-200 space-y-4">
                    <h4 className="text-sm font-medium text-gray-600">
                      Planning & Measurement
                    </h4>

                    {/* Planning Cycle */}
                    {planningConfig && planningCycles.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Planning Cycle
                        </label>
                        <PlanningCycleSelector
                          organizationId={organizationId}
                          currentCycleId={formData.planning_cycle_id || ''}
                          cycles={planningCycles}
                          configuredCycleTypes={
                            planningConfig.cycle_types || []
                          }
                          onCycleSelect={(cycleId) =>
                            setFormData({
                              ...formData,
                              planning_cycle_id: cycleId || undefined,
                            })
                          }
                          onCyclesUpdate={onCyclesUpdate}
                        />
                      </div>
                    )}

                    {/* Status Selection - Only show for existing OKRs */}
                    {okr && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              status: e.target.value as any,
                            })
                          }
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                        >
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    )}

                    {/* Commitment Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Commitment Type
                        <span className="ml-1 inline-flex items-center">
                          <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        </span>
                      </label>
                      <div className="space-y-2">
                        <label className="relative flex items-start cursor-pointer">
                          <input
                            type="radio"
                            name="commitment_type"
                            value="committed"
                            checked={formData.commitment_type === 'committed'}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                commitment_type: e.target.value as
                                  | 'committed'
                                  | 'aspirational',
                              });
                            }}
                            className="mt-1 mr-3"
                          />
                          <div>
                            <span className="font-medium">Committed</span>
                            <p className="text-xs text-gray-500">
                              Must achieve 100% - Pass/fail with no partial
                              credit
                            </p>
                          </div>
                        </label>
                        <label className="relative flex items-start cursor-pointer">
                          <input
                            type="radio"
                            name="commitment_type"
                            value="aspirational"
                            checked={
                              formData.commitment_type === 'aspirational'
                            }
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                commitment_type: e.target.value as
                                  | 'committed'
                                  | 'aspirational',
                              });
                            }}
                            className="mt-1 mr-3"
                          />
                          <div>
                            <span className="font-medium">Aspirational</span>
                            <p className="text-xs text-gray-500">
                              Stretch goal - 60-70% is considered success
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Info message about Key Results */}
                  {!okr && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium">
                            Key Results can be added after creation
                          </p>
                          <p className="mt-1 text-xs">
                            After creating the objective, you&apos;ll be taken
                            to the detail page where you can add and manage Key
                            Results with full flexibility.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving
                        ? 'Saving...'
                        : okr
                          ? 'Update Objective'
                          : 'Create Objective'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
